import { BehaviorSubject, Observable, throwError, timer } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { DataTablePreference, UserPreference } from '../models/user-preference';
import { User } from '../models/user.model';
import { NGXLogger } from 'ngx-logger';
import {initLaasPortalVersion, isNullOrEmpty} from '../utils/common-functions';
import { GlobalConstants } from '../constants/global-constants.constant';
import { LocalStorageService } from './local-storage.service';
import {CommonDataService} from './common-data.service';
import { retry } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserService {

  user: BehaviorSubject<User>;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private logger: NGXLogger,
    private commonDataService: CommonDataService,) {
    this.user = new BehaviorSubject<User>(this.userInfo);
  }

  get userInfo(): User {
    const userData = this.localStorageService.getItem(GlobalConstants.currentUser);
    return isNullOrEmpty(userData) ? new User() : userData;
  }

  set userInfo(value: User) {
    initLaasPortalVersion(this.commonDataService, value.userType);
    this.localStorageService.setItem(GlobalConstants.currentUser, value);
    this.user.next(value);
  }

  get preference(): UserPreference {
    const preferenceData = this.localStorageService.getItem(GlobalConstants.userPreference);
    return isNullOrEmpty(preferenceData) ? new UserPreference() : preferenceData;
  }

  set preference(value: UserPreference) {
    this.localStorageService.setItem(GlobalConstants.userPreference, value);
    this.saveUserPreference()
      .subscribe({
        next: () => {

        },
        error: err => {

        }
      });
  }

  getUserProfile(): Observable<any> {
    return this.http.get<any>(`${environment.authUrl}/authorization`);
  }

  getUserPreferences(): Observable<any> {
  const url = `${environment.logisticsApiUrl}/Common/getUserPreference/${this.userInfo.id}`;
    return this.http.get(url).pipe(
      retry({
        count: 3, // 最多重試 3 次
        delay: (error, retryCount) => {
          // 1. 如果是 4xx 客戶端錯誤 (如 401, 403, 404)，不重試，直接拋出錯誤
          if (error.status >= 400 && error.status < 500) {
            return throwError(() => error);
          }
          console.warn(`API Error (${error.status}), retrying... attempt ${retryCount}`);
          // 2. 其他錯誤 (5xx, 0)，等待 0.5 秒後重試
          return timer(500);
        }
      })
    );
  }

  logout(): void {
    this.localStorageService.removeItem(GlobalConstants.currentUser);
    this.localStorageService.removeItem(GlobalConstants.userPreference);
    this.logoutAction()
      .subscribe({
        next: () => {
        },
        error: (err: HttpErrorResponse) => {
          this.logger.error(JSON.stringify(err));
        }
      });

    this.userInfo = new User();
  }

  private logoutAction(): Observable<any> {
    const url = '/api/logout';
    return this.http.get(url);
  }

  private saveUserPreference(): Observable<any> {
    // const url = `/api/userpreference/${this.userInfo.id}`;
    const url = `${environment.logisticsApiUrl}/Common/saveUserPreference/${this.userInfo.id}`;
    return this.http.put(url, { preference: this.preference, userName: this.userInfo.userName });
  }

  getUserOptions(): Observable<any> {
    const url = `${environment.authUrl}/user/getUserOptions`;
    return this.http.post(url, null);
  }

  forgotPassword(loginAccount: string): Observable<any> {
    return this.http.post<any>(`${environment.authUrl}/user/forgotPassword`, loginAccount);
  }

  updateUserPreferenceInLocalStorage(preference: UserPreference): void {
    this.localStorageService.setItem(GlobalConstants.userPreference, preference);
  }
}


